import React, { useMemo } from 'react';
import { Activity, Clock, CalendarDays, CalendarRange } from 'lucide-react';
import { useWaHeatmapStats } from '../../hooks/whatsapp/useWaHeatmapStats';
import { DAYS_OF_WEEK } from '../../services/whatsapp/waConstants';

// Mapas de calor das conversas (#62). Tudo em GRID CSS colorido, sem nenhuma
// dependencia nova de chart: a intensidade da cor de cada celula e proporcional
// ao volume (escala do mais claro ao wa-500), e o tooltip nativo (title) mostra
// o valor. Os dados ja vem completos e no fuso de Brasilia direto do backend.

/**
 * Calcula a cor de fundo de uma celula a partir do seu valor e do maximo da
 * serie. Interpola o tom da paleta WhatsApp (verde) do quase-branco (volume
 * baixo) ate o verde forte (volume = max). Celula com 0 fica cinza bem claro
 * para indicar "sem volume" sem sumir do grid.
 */
function heatColor(value: number, max: number): string {
  if (value <= 0) return '#f3f4f6'; // gray-100: vazio
  if (max <= 0) return '#f3f4f6';
  // intensidade 0..1 — usamos um piso de 0.15 para a menor celula ja ter cor
  const t = 0.15 + 0.85 * (value / max);
  // wa-500 aprox. #25D366 (verde WhatsApp). Interpola do branco ate esse verde.
  const r = Math.round(255 + (37 - 255) * t);
  const g = Math.round(255 + (211 - 255) * t);
  const b = Math.round(255 + (102 - 255) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

// Texto escuro em celulas claras e branco nas escuras, para o numero ler bem.
function textColor(value: number, max: number): string {
  if (value <= 0 || max <= 0) return '#9ca3af'; // gray-400
  const t = value / max;
  return t > 0.55 ? '#ffffff' : '#1f2937'; // gray-800
}

interface HeatCellProps {
  label: string; // rotulo curto exibido na celula (hora/dia)
  value: number;
  max: number;
  title: string; // tooltip nativo com o valor completo
}

function HeatCell({ label, value, max, title }: HeatCellProps) {
  return (
    <div
      title={title}
      className="flex aspect-square min-h-[40px] flex-col items-center justify-center rounded-lg text-[11px] font-medium transition-transform hover:scale-105"
      style={{ backgroundColor: heatColor(value, max), color: textColor(value, max) }}
    >
      <span className="opacity-70">{label}</span>
      <span className="text-xs font-bold">{value}</span>
    </div>
  );
}

// Cartao branco padrao (rounded-2xl + shadow-wa-apple) que envolve cada heatmap.
function HeatCard({
  icon: Icon,
  color,
  title,
  subtitle,
  children,
}: {
  icon: typeof Activity;
  color: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
      <div className="mb-4 flex items-center gap-2">
        <div className="rounded-xl bg-green-50 p-2">
          <Icon size={18} style={{ color }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

interface WaHeatmapsTabProps {
  /** Janela em dias vinda do seletor de período da página (default 30). */
  days?: number;
}

export default function WaHeatmapsTab({ days = 30 }: WaHeatmapsTabProps) {
  const { byHour, byWeekday, byMonthday, isLoading, error } = useWaHeatmapStats(days);

  const maxHour = useMemo(() => Math.max(0, ...byHour.map(h => h.count)), [byHour]);
  const maxWeekday = useMemo(() => Math.max(0, ...byWeekday.map(w => w.count)), [byWeekday]);
  const maxMonthday = useMemo(() => Math.max(0, ...byMonthday.map(d => d.count)), [byMonthday]);

  // Soma total p/ detectar "sem dados" mesmo quando o array vem cheio de zeros.
  const total = useMemo(() => byHour.reduce((acc, h) => acc + h.count, 0), [byHour]);

  if (isLoading) {
    return (
      <div className="animate-slide-up flex h-[300px] items-center justify-center rounded-2xl bg-white text-sm text-gray-400 shadow-wa-apple">
        Carregando mapas de calor…
      </div>
    );
  }

  // 403: usuario sem permissao (endpoint exige admin/supervisor).
  if (error === 'sem_permissao') {
    return (
      <div className="animate-slide-up flex h-[300px] flex-col items-center justify-center gap-2 rounded-2xl bg-white text-center shadow-wa-apple">
        <Activity size={28} className="text-gray-300" />
        <p className="text-sm font-medium text-gray-600">Acesso restrito</p>
        <p className="text-xs text-gray-400">
          Os mapas de calor sao visiveis apenas para administradores e supervisores.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="animate-slide-up flex h-[300px] items-center justify-center rounded-2xl bg-white text-sm text-red-400 shadow-wa-apple">
        Erro ao carregar os mapas de calor.
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="animate-slide-up flex h-[300px] flex-col items-center justify-center gap-2 rounded-2xl bg-white text-center shadow-wa-apple">
        <Activity size={28} className="text-gray-300" />
        <p className="text-sm font-medium text-gray-600">Sem conversas no periodo</p>
        <p className="text-xs text-gray-400">Nao ha volume nos ultimos 30 dias para exibir.</p>
      </div>
    );
  }

  return (
    <div className="animate-slide-up space-y-6">
      {/* Por hora do dia (0-23): grid de 12 colunas (2 linhas de 12). */}
      <HeatCard
        icon={Clock}
        color="#34C759"
        title="Por Hora do Dia"
        subtitle="Volume de conversas iniciadas por hora (horario de Brasilia)"
      >
        <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-12">
          {byHour.map(h => (
            <HeatCell
              key={h.hour}
              label={`${String(h.hour).padStart(2, '0')}h`}
              value={h.count}
              max={maxHour}
              title={`${String(h.hour).padStart(2, '0')}:00 — ${h.count} conversa(s)`}
            />
          ))}
        </div>
      </HeatCard>

      {/* Por dia da semana (Seg-Dom): 7 colunas. */}
      <HeatCard
        icon={CalendarDays}
        color="#007AFF"
        title="Por Dia da Semana"
        subtitle="Volume de conversas iniciadas por dia da semana"
      >
        <div className="grid grid-cols-7 gap-1.5">
          {byWeekday.map(w => (
            <HeatCell
              key={w.weekday}
              label={DAYS_OF_WEEK[w.weekday] ?? String(w.weekday)}
              value={w.count}
              max={maxWeekday}
              title={`${DAYS_OF_WEEK[w.weekday] ?? w.weekday} — ${w.count} conversa(s)`}
            />
          ))}
        </div>
      </HeatCard>

      {/* Por dia do mes (1-31): estilo calendario, 7 colunas. */}
      <HeatCard
        icon={CalendarRange}
        color="#AF52DE"
        title="Por Dia do Mes"
        subtitle="Volume de conversas iniciadas por dia do mes (1 a 31)"
      >
        <div className="grid grid-cols-7 gap-1.5">
          {byMonthday.map(d => (
            <HeatCell
              key={d.day}
              label={String(d.day)}
              value={d.count}
              max={maxMonthday}
              title={`Dia ${d.day} — ${d.count} conversa(s)`}
            />
          ))}
        </div>
      </HeatCard>
    </div>
  );
}
